/**
 * The GreenPak trail difficulty system.
 *
 * ONE STORED VALUE. A trail row carries `difficulty` as an integer 1-5 and
 * nothing else. Every label, every screen-reader sentence and every meter
 * segment on every surface is derived here, so a row can never end up saying
 * "4" beside the word "Moderate". That failure mode is not hypothetical in
 * this codebase — tour cards spent months showing a duration-derived
 * difficulty that contradicted the CMS value on the tour's own page.
 *
 * NO ALGORITHM, ON PURPOSE. There is no function here that computes a rating
 * from distance and elevation gain, because Pakistan's trail authorities do
 * not publish distances or gradients for these trails (see the research notes
 * in scripts/seed-islamabad-trails.ts). A scorer fed mostly-null inputs would
 * produce confident numbers from nothing. The product rule is instead an
 * editorially reviewed level with a written rationale — `difficultyRationale`
 * on the row — which is why the factor fields below are descriptive inputs a
 * human weighed, not terms in a formula.
 */

export const TRAIL_DIFFICULTY_LEVELS = [1, 2, 3, 4, 5] as const;
export type TrailDifficultyLevel = (typeof TRAIL_DIFFICULTY_LEVELS)[number];

type LevelMeaning = {
  /** The controlled label. Never stored — always read from here. */
  label: string;
  /** What the level means in general, before any trail-specific note. */
  summary: string;
};

const SCALE: Record<TrailDifficultyLevel, LevelMeaning> = {
  1: {
    label: "Easy",
    summary: "Short and mostly level, on a clear path, with no scrambling and little sustained climbing.",
  },
  2: {
    label: "Easy–Moderate",
    summary: "Gentle climbing on a clear path, comfortable for most people who walk regularly.",
  },
  3: {
    label: "Moderate",
    summary: "Sustained climbing on uneven ground. Expect to work, and expect to stop.",
  },
  4: {
    label: "Challenging",
    summary: "Long or steep, on rough ground, with sections that need care and a good level of fitness.",
  },
  5: {
    label: "Strenuous",
    summary: "Continuous steep or rough ground over a long day, for people who hike often and prepare for it.",
  },
};

export function isTrailDifficultyLevel(value: unknown): value is TrailDifficultyLevel {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    (TRAIL_DIFFICULTY_LEVELS as readonly number[]).includes(value)
  );
}

/** The controlled label for a level. Returns null for anything unrated. */
export function trailDifficultyLabel(level: unknown): string | null {
  return isTrailDifficultyLevel(level) ? SCALE[level].label : null;
}

export function trailDifficultySummary(level: unknown): string | null {
  return isTrailDifficultyLevel(level) ? SCALE[level].summary : null;
}

/** Every level, for admin selects and for the methodology table. */
export function trailDifficultyOptions(): Array<{
  level: TrailDifficultyLevel;
  label: string;
  summary: string;
}> {
  return TRAIL_DIFFICULTY_LEVELS.map((level) => ({ level, ...SCALE[level] }));
}

export type TrailDifficultySegment = {
  /** 1-based, so it matches the number a reader sees. */
  index: TrailDifficultyLevel;
  /** Below the rating, the rating itself, above it. */
  state: "filled" | "current" | "empty";
};

export type TrailDifficultyMeterModel = {
  level: TrailDifficultyLevel;
  label: string;
  summary: string;
  /** "3 / 5" — the numeric position, always rendered as text. */
  position: string;
  segments: TrailDifficultySegment[];
  /** The trail's own one-line explanation, when an editor wrote one. */
  note: string | null;
  /**
   * The whole rating as one sentence. Rendered into a visually-hidden element
   * so the meter is never colour-or-shape-only: a screen reader gets the
   * number, the word and the explanation without touching the segments.
   */
  accessibleText: string;
};

/**
 * Builds everything a difficulty meter needs to render, in any variant.
 *
 * Returns null rather than a zero-state for an unrated trail. "Unrated" and
 * "Easy" must never look alike, and a meter with no filled segments reads as
 * the latter — so the caller omits the component instead.
 */
export function buildTrailDifficultyMeter(input: {
  level: unknown;
  note?: string | null;
}): TrailDifficultyMeterModel | null {
  if (!isTrailDifficultyLevel(input.level)) return null;
  const level = input.level;
  const { label, summary } = SCALE[level];
  const note = input.note?.trim() ? input.note.trim() : null;

  const segments: TrailDifficultySegment[] = TRAIL_DIFFICULTY_LEVELS.map((index) => ({
    index,
    state: index < level ? "filled" : index === level ? "current" : "empty",
  }));

  // The note is already a "·"-separated fragment list ("Steady climbing ·
  // uneven trail"), so it is folded in as its own sentence rather than being
  // punctuated into the one above it.
  const accessibleText = note
    ? `GreenPak difficulty ${level} of 5: ${label}. ${summary} ${note}.`
    : `GreenPak difficulty ${level} of 5: ${label}. ${summary}`;

  return {
    level,
    label,
    summary,
    position: `${level} / 5`,
    segments,
    note,
    accessibleText,
  };
}

/**
 * The named things an editor weighed when choosing the level. Each is itself
 * 1-5 and each is optional: a trail whose terrain nobody has assessed shows
 * three factors, not four with a blank.
 */
export const TRAIL_DIFFICULTY_FACTORS = [
  { key: "steepness", label: "Steepness", hint: "How hard the climbing is at its worst." },
  { key: "endurance", label: "Endurance", hint: "How long the effort has to be sustained." },
  { key: "terrain", label: "Terrain", hint: "Surface, footing and obstacles underfoot." },
  { key: "navigation", label: "Navigation", hint: "How easy the route is to follow unaided." },
] as const;

export type TrailDifficultyFactorKey = (typeof TRAIL_DIFFICULTY_FACTORS)[number]["key"];

export type TrailDifficultyFactorModel = {
  key: TrailDifficultyFactorKey;
  label: string;
  hint: string;
  level: TrailDifficultyLevel;
  /** The same controlled vocabulary as the overall rating. */
  levelLabel: string;
  position: string;
};

/** Only the factors that carry a real 1-5 value, in a stable order. */
export function buildTrailDifficultyFactors(
  values: Partial<Record<TrailDifficultyFactorKey, unknown>>,
): TrailDifficultyFactorModel[] {
  const out: TrailDifficultyFactorModel[] = [];
  for (const factor of TRAIL_DIFFICULTY_FACTORS) {
    const value = values[factor.key];
    if (!isTrailDifficultyLevel(value)) continue;
    out.push({
      key: factor.key,
      label: factor.label,
      hint: factor.hint,
      level: value,
      levelLabel: SCALE[value].label,
      position: `${value} / 5`,
    });
  }
  return out;
}
